import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../store';
import { CleverService } from '../../services/clever.service';
import { autoFetchUserDetails, forceFetchUserDetails } from './user.thunk';
import { Toast } from '../../components/Common/Toast';

// Sign in with Clever
export const signInWithClever = createAsyncThunk(
  'clever/signInWithClever',
  async (code: string, { dispatch, rejectWithValue }) => {
    try {
      // Sign in with Clever
      const authResponse = await CleverService.signIn(code);
      
      // Complete the authentication flow
      await CleverService.completeAuth(authResponse, dispatch);
      
      // Fetch user details
      dispatch(forceFetchUserDetails(() => {}));
      
      Toast(authResponse.isNewUser ? 'Account created successfully!' : 'Login successful!');
      
      return {
        user: authResponse.user,
        isNewUser: authResponse.isNewUser,
      };
    } catch (error: any) {
      Toast(error.message || 'Clever authentication failed', true);
      return rejectWithValue(error.message || 'Authentication failed');
    }
  }
);

// Link existing account to Clever
export const linkAccountToClever = createAsyncThunk(
  'clever/linkAccountToClever',
  async ({ email, code }: { email: string; code: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await CleverService.linkAccount(email, code);
      
      // Refresh user details
      dispatch(autoFetchUserDetails());
      
      Toast('Account linked to Clever successfully!');
      
      return response.user;
    } catch (error: any) {
      Toast(error.message || 'Failed to link account', true);
      return rejectWithValue(error.message || 'Failed to link account');
    }
  }
);

// Unlink Clever account
export const unlinkCleverAccount = createAsyncThunk(
  'clever/unlinkCleverAccount',
  async (email: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await CleverService.unlinkAccount(email);
      
      // Clear Clever data
      CleverService.clearCleverData();
      
      // Refresh user details
      dispatch(autoFetchUserDetails());
      
      Toast('Clever account unlinked successfully!');
      
      return response.user;
    } catch (error: any) {
      Toast(error.message || 'Failed to unlink account', true);
      return rejectWithValue(error.message || 'Failed to unlink account');
    }
  }
);

// Check if user is authenticated via Clever
export const checkCleverAuth = createAsyncThunk(
  'clever/checkAuth',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const isCleverUser = CleverService.isCleverUser();
    const cleverUserData = CleverService.getCleverUserData();
    
    return {
      isCleverUser,
      cleverUserData,
    };
  }
); 